import Paper from '@/components/Paper'
import SubHeaders from '@/components/Headers/SubHeaders'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { FilterBar } from '@/components/ui/data-table-customized'
import { ExcelExportButton } from '@/components/ui/data-table-customized'
import AddIcons from '@/icons/AddIcons'
import { paths } from '@/utils/path'
import { IPermissionProps } from '@/types/shared'
import { useTourHandler } from '@/contexts/tour-handler'
import { useEffect } from 'react'

import { DAYOFF_EXCEL_COLUMNS } from '../../constants'
import { useDayOffList } from '../../hooks/use-dayoff-list'
import Statistical from '../Create/Statistical'
import StatsHRM from './StatsHRM'
import { useDayOffListTour } from '../../hooks/use-dayoff-list-tour'
import { useDayOffListQuickHelp } from '../../hooks/use-dayoff-list-quick-help'

interface DayOffListProps extends IPermissionProps {
    pageType: 'member' | 'hrm'
    hideHeader?: boolean
}

const List = ({ pageType = 'member', hideHeader = false }: DayOffListProps) => {
    const {
        dayoffs,
        pagination,
        stateComponent,
        control,
        setFilter,
        params,
        queryParams,
        searchInputProps,
        selectedKeys,
        setSelectedKeys,
        columns,
        expandedColumns,
        actions,
        selectionActions,
        filterItems,
        filterToggles,
        previewColumns,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    } = useDayOffList(pageType)

    const isHRM = pageType === 'hrm'

    // Tour hooks
    const { startTour } = useDayOffListTour(pageType)
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Quick help hook
    useDayOffListQuickHelp(pageType)

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    return (
        <div className="space-y-2" data-tour="dayoff-list">
            {!hideHeader && (
                <SubHeaders
                    actions={
                        !isHRM
                            ? [
                                  {
                                      icon: <AddIcons />,
                                      label: t('create'),
                                      variant: 'contained',
                                      onClick: () =>
                                          navigate(paths.dayOffCreatePage),
                                      'data-tour': 'create-dayoff-btn',
                                  },
                              ]
                            : []
                    }
                    breadcrumbs={[
                        {
                            path: isHRM
                                ? paths.dayOffHRMList
                                : paths.dayOffAccountList,
                            title: t('day_off_list'),
                        },
                    ]}
                />
            )}

            {!isHRM && <Statistical />}
            {isHRM && (
                <div data-tour="dayoff-stats">
                    <StatsHRM queryParams={queryParams} />
                </div>
            )}

            <Paper className="space-y-2">
                <div data-tour="dayoff-filter-bar">
                    <FilterBar
                        storageKey="dayoff-list"
                        control={control}
                        setFilter={setFilter}
                        hideSearch
                        items={filterItems}
                    />
                </div>

                {stateComponent || (
                    <div data-tour="dayoff-data-table">
                        <DataTableCustomized
                            columns={columns}
                            rows={dayoffs}
                            rowKey={(row) => row.day_off_id}
                            resizable
                            size="small"
                            actions={actions}
                            expandable={{
                                expandedColumns,
                                expandedRowData: (row) =>
                                    row.day_off_range_time,
                                rowExpandable: (row) =>
                                    row.day_off_range_time?.length > 0,
                            }}
                            toolbar={{
                                search: {
                                    ...searchInputProps,
                                    placeholder: t(
                                        'search_employee_name_department_type_reason'
                                    ),
                                    tourId: 'dayoff-search',
                                },
                                settings: isHRM
                                    ? {
                                          show: true,
                                          storageKey: 'dayoff-list',
                                          filterToggles,
                                          tourId: 'dayoff-settings',
                                          slotComponents: [
                                              <ExcelExportButton
                                                  key="export"
                                                  apiEndpoint="dayoffs"
                                                  columns={DAYOFF_EXCEL_COLUMNS}
                                                  queryParams={queryParams}
                                                  feature="dayoffs"
                                              />,
                                          ],
                                      }
                                    : {
                                          show: true,
                                          storageKey: 'dayoff-list',
                                          filterToggles,
                                          tourId: 'dayoff-settings',
                                      },
                            }}
                            selection={
                                isHRM
                                    ? {
                                          selectedKeys,
                                          onSelectionChange: setSelectedKeys,
                                          getRowKey: (row) =>
                                              String(row.day_off_id),
                                          canSelect: (row) =>
                                              [
                                                  'leader-approved',
                                                  'pending',
                                              ].includes(row.day_off_status),
                                          actions: selectionActions,
                                          previewColumns,
                                      }
                                    : undefined
                            }
                            sorting={{
                                sortBy: params.sort_by,
                                sortOrder: params.sort_order,
                                onSortChange: handleSortChange,
                            }}
                            pagination={{
                                page: params.page,
                                pageSize: params.page_size,
                                total: pagination?.total || 0,
                                onPageChange: handlePageChange,
                                onPageSizeChange: handlePageSizeChange,
                            }}
                        />
                    </div>
                )}
            </Paper>
        </div>
    )
}

export const DayOffList = (props: DayOffListProps) => <List {...props} />
